import {
  GetClientFlags200Response,
  SubscriptionCheckoutMode,
  SubscriptionDetails,
  SubscriptionPlanType,
} from "@coeff/api";

import { getIsGmailLikeDomainFromEmail } from "./billingHelpers";

/**
 * Mirrors server-side `_subscription_allows_self_serve_cancel`:
 *  - Not sales-led (checkout_mode !== ContactUs)
 *  - Not Enterprise / Custom plan (Plus is remapped to Starter by the server)
 *  - Gmail-like domain OR single license
 *
 * `checkout_mode` is derived server-side from `is_sales_led_deal || enterprise || custom`
 * (see `_fetch_subscription_details_for_user`), so checking `checkout_mode === ContactUs`
 * is equivalent to the server's `is_sales_led_deal` check.
 *
 * Note: client checks the *user's* domain (via clientFlags) while the server
 * checks the *subscription's* domain. These can diverge after an admin payer
 * transfer across domains. The server is authoritative; the modal handles
 * rejection gracefully.
 */
export function ableToSelfCancel(params: {
  subscriptionDetails?: SubscriptionDetails | null;
  clientFlags?: GetClientFlags200Response | null;
  userEmail: string;
  featureEnabled?: boolean;
}): boolean {
  const { subscriptionDetails, clientFlags, userEmail, featureEnabled } = params;

  if (!featureEnabled) {
    return false;
  }

  if (!subscriptionDetails) {
    return false;
  }

  if (subscriptionDetails.checkout_mode === SubscriptionCheckoutMode.ContactUs) {
    return false;
  }

  const planType = subscriptionDetails.plan_type;
  if (
    planType !== SubscriptionPlanType.Pro &&
    planType !== SubscriptionPlanType.Plus &&
    planType !== SubscriptionPlanType.Starter
  ) {
    return false;
  }

  const isGmailLike = clientFlags?.is_gmail_like_domain ?? getIsGmailLikeDomainFromEmail(userEmail);
  const maxUsers = subscriptionDetails.max_users ?? 0;
  const isSingleLicense = maxUsers === 1;

  return isGmailLike || isSingleLicense;
}
